/**
 * Verkleinert eine Bilddatei clientseitig (Canvas) auf ein JPEG und liefert
 * Base64 ohne data:-Prefix. Für den Entwurf-Upload im Scan-Schritt, wo es noch
 * keine Analyse-ID (und damit keinen Server-Resize) gibt.
 */
export async function resizeImageToBase64(
  file: File,
  maxWidth = 800,
  quality = 0.72
): Promise<string | null> {
  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
      el.src = dataUrl;
    });
    const scale = Math.min(1, maxWidth / (img.naturalWidth || maxWidth));
    const w = Math.max(1, Math.round((img.naturalWidth || maxWidth) * scale));
    const h = Math.max(1, Math.round((img.naturalHeight || maxWidth) * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    const out = canvas.toDataURL('image/jpeg', quality);
    const m = /^data:[^;]+;base64,(.*)$/s.exec(out);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/** Ab dieser Größe lohnt sich clientseitige Kompression vor dem Upload. */
export const IMAGE_COMPRESS_THRESHOLD_BYTES = 2 * 1024 * 1024;

function isCompressibleImage(file: File): boolean {
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('image/')) return t !== 'image/svg+xml';
  return /\.(jpe?g|png|webp|gif|bmp|tiff?)$/i.test(file.name.trim());
}

/**
 * Komprimiert eine Bilddatei clientseitig zu einem JPEG-File (max. Kantenlänge
 * `maxDim`). Nicht-Bilder, kleine Bilder und Fehlschläge (z. B. HEIC, das der
 * Browser nicht dekodieren kann) liefern unverändert das Original zurück —
 * die Funktion ist damit gefahrlos auf ganze Datei-Listen anwendbar.
 */
export async function compressImageFile(
  file: File,
  opts?: { maxDim?: number; quality?: number; thresholdBytes?: number }
): Promise<File> {
  const threshold = opts?.thresholdBytes ?? IMAGE_COMPRESS_THRESHOLD_BYTES;
  if (!isCompressibleImage(file) || file.size <= threshold) return file;
  const maxDim = opts?.maxDim ?? 2000;
  const quality = opts?.quality ?? 0.8;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;
    const jpgName = file.name.replace(/\.(jpe?g|png|webp|gif|bmp|tiff?)$/i, '') + '.jpg';
    const out = new File([blob], jpgName, { type: 'image/jpeg', lastModified: file.lastModified });
    // Unterordner-Pfad fürs UI-Label erhalten (fileDisplayLabel).
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
    if (rel) {
      try {
        Object.defineProperty(out, 'webkitRelativePath', { value: rel.replace(/[^/]+$/, jpgName) });
      } catch {
        /* ignore */
      }
    }
    return out;
  } catch {
    return file;
  }
}

/** Komprimiert alle Bilder einer Liste (PDFs u. a. bleiben unverändert). */
export function compressImagesInFiles(files: File[]): Promise<File[]> {
  return Promise.all(files.map((f) => compressImageFile(f)));
}
